// ----- MenuCart.js -----
import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../../utils/FormatCurrency";
import { getCartItems, removeFromCart } from "../../../services/Cart";
import { toast } from "react-toastify";
import LoginReminderPopup from "../../../components/common/LoginReminderPopup";
import { getUserFromToken } from "../../../services/User";

const MenuCart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation(); // 👉 để reset popup khi đổi route

  const [cartItems, setCartItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showLoginReminder, setShowLoginReminder] = useState(false);

  // --- Fetch Cart Items ---
  const fetchCartItems = useCallback(async () => {
    try {
      const items = await getCartItems();
      setCartItems(items);
      setSelectedItems(items.map((item) => item.cartId));
    } catch (error) {
      console.error("Error fetching cart items:", error);
    }
  }, []);

  // --- Listen for cart events ---
  useEffect(() => {
    const handleCartOpened = () => setIsCartOpen(true);
    window.addEventListener("cart-opened", handleCartOpened);
    window.addEventListener("cart-updated", fetchCartItems);

    return () => {
      window.removeEventListener("cart-opened", handleCartOpened);
      window.removeEventListener("cart-updated", fetchCartItems);
    };
  }, [fetchCartItems]);

  // --- Fetch when cart open ---
  useEffect(() => {
    if (isCartOpen) fetchCartItems();
  }, [isCartOpen, fetchCartItems]);

  // --- Reset popup khi đổi route ---
  useEffect(() => {
    setShowLoginReminder(false);
  }, [location.pathname]);

  // --- Tính tổng tiền ---
  const cartTotalPrice = useMemo(() => {
    return cartItems
      .filter((item) => selectedItems.includes(item.cartId))
      .reduce((sum, item) => {
        const finalPrice =
          item.finalPrice > 0 ? item.finalPrice : item.basePrice;
        return sum + finalPrice * item.quantity;
      }, 0);
  }, [cartItems, selectedItems]);

  // --- Xóa sản phẩm ---
  const handleRemoveFromCart = useCallback(
    async (cartId) => {
      if (loading) return;
      setLoading(true);
      try {
        await removeFromCart(cartId);
        await fetchCartItems();
        toast.success(t("removedFromCart"));
      } catch (error) {
        console.error("Error removing item from cart:", error);
        toast.error(t("errorRemovingFromCart"));
      } finally {
        setLoading(false);
      }
    },
    [fetchCartItems, loading, t]
  );

  // --- Toggle chọn sản phẩm ---
  const handleToggleSelect = (cartId) => {
    setSelectedItems((prev) =>
      prev.includes(cartId)
        ? prev.filter((id) => id !== cartId)
        : [...prev, cartId]
    );
  };

  // --- Checkout ---
  const handleCheckout = () => {
    const user = getUserFromToken();
    if (!user) {
      setShowLoginReminder(true); // 👉 chỉ bật khi checkout chưa login
      return;
    }

    const selectedCartItems = cartItems
      .filter((item) => selectedItems.includes(item.cartId))
      .map((item) => ({
        ...item,
        variant: {
          price: item.finalPrice > 0 ? item.finalPrice : item.basePrice,
          color: item.color,
          size: item.size,
          product: {
            name: item.productName,
            productImages: [{ imageUrl: item.imageUrl }],
          },
        },
      }));

    if (selectedCartItems.length === 0) {
      toast.warning(t("pleaseSelectProduct"));
      return;
    }

    navigate("/checkout", { state: { selectedCartItems } });
  };

  return (
    <>
      <div className="shopping-cart-content">
        {cartItems.length > 0 ? (
          <Fragment>
            <ul>
              {cartItems.map((item) => {
                const finalPrice =
                  item.finalPrice > 0 ? item.finalPrice : item.basePrice;
                return (
                  <li className="single-shopping-cart" key={item.cartId}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.cartId)}
                        onChange={() => handleToggleSelect(item.cartId)}
                        style={{
                          width: "16px",
                          height: "16px",
                          marginTop: "6px",
                          cursor: "pointer",
                        }}
                      />
                      <div className="shopping-cart-img">
                        <Link to={`/product/${item.productId}`}>
                          <img
                            src={item.imageUrl}
                            alt={item.productName}
                            width={60}
                            height={60}
                            style={{
                              objectFit: "contain",
                              borderRadius: 4,
                              border: "1px solid #ddd",
                            }}
                          />
                        </Link>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontSize: 14, marginBottom: 4 }}>
                          {item.productName}
                        </h4>
                        <div style={{ fontSize: 13, color: "#444" }}>
                          {t("quantity")}: {item.quantity} <br />
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            {item.finalPrice < item.basePrice && (
                              <span
                                style={{
                                  fontSize: 13,
                                  color: "#888",
                                  textDecoration: "line-through",
                                  lineHeight: 1.2,
                                }}
                              >
                                {formatPrice(item.basePrice)}
                              </span>
                            )}
                            <span
                              style={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: "#c00",
                                lineHeight: 1.2,
                              }}
                            >
                              {formatPrice(
                                item.finalPrice > 0
                                  ? item.finalPrice
                                  : item.basePrice
                              )}
                            </span>
                            <div>
                              <span style={{ fontWeight: 500 }}>
                                {t("color")}:
                              </span>{" "}
                              <span>{item.color}</span>
                            </div>
                            <div>
                              <span style={{ fontWeight: 500 }}>
                                {t("size")}:
                              </span>{" "}
                              <span>{item.size}</span>
                            </div>
                          </div>
                          <br />
                        </div>
                      </div>
                      <div>
                        <button
                          onClick={() => handleRemoveFromCart(item.cartId)}
                          disabled={loading}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#900",
                            fontSize: 18,
                            marginLeft: 4,
                          }}
                        >
                          <i className="fa fa-times-circle" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="shopping-cart-total">
              <h4>
                {t("total")}:{" "}
                <span className="shop-total">{formatPrice(cartTotalPrice)}</span>
              </h4>
            </div>
            <div className="shopping-cart-btn btn-hover text-center">
              <Link
                className="default-btn"
                to={`${process.env.PUBLIC_URL}/cart`}
              >
                {t("view_cart")}
              </Link>
              <Link
                className="default-btn"
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleCheckout();
                }}
              >
                {t("checkout")}
              </Link>
            </div>
          </Fragment>
        ) : (
          <p className="text-center">{t("cart.empty")}</p>
        )}
      </div>

      {/* Popup nhắc login */}
      <LoginReminderPopup
        show={showLoginReminder}
        onClose={() => setShowLoginReminder(false)}
      />
    </>
  );
};

export default MenuCart;
